"use client";

import { useState } from "react";
import {
  Checkbox,
  CopyButton,
  Notice,
  PillGroup,
  TextAreaField,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  CARD,
  HINT,
  RESULT_CARD,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import {
  buildCounterParagraphs,
  COUNTER_CONCESSION_FRAMES,
  COUNTER_FRAMES,
  COUNTER_REBUTTAL_FRAMES,
  COUNTER_RETURN_FRAMES,
  COUNTER_STRATEGIES,
  type CounterField,
  type CounterInput,
  type CounterStrategy,
  missingCounterFields,
  paragraphText,
} from "@/lib/tool-kit/essay/counterargument";
import { joinSeries } from "@/lib/tool-kit/essay/sentence";

/** English strings. A translated content file overrides these through `ui`. */
export const COUNTERARGUMENT_GENERATOR_STRINGS = {
  strategyLabel: "How do you want to answer the other side?",
  strategies: {
    concede: "Concede and refute",
    partial: "Partial concession",
    turn: "Turn the tables",
  },
  strategyHints: {
    concede:
      "Acknowledge the opposing view fairly, then show with evidence why it does not hold.",
    partial:
      "Grant that the other side has a real point, then show it does not change your conclusion.",
    turn: "Show that the other side's own best reason actually supports your claim.",
  },
  claimLabel: "Your claim",
  claimPlaceholder: "e.g. schools should start no earlier than 8:30 a.m.",
  claimHint:
    "The position you are defending — your thesis or this paragraph's claim, written as a clause.",
  opposingLabel: "The opposing view",
  opposingPlaceholder: "e.g. early start times should be kept",
  opposingHint:
    "What a reasonable person on the other side believes. State it fairly — a straw man convinces no one.",
  reasonLabel: "Their best reason",
  reasonPlaceholder:
    "e.g. many families rely on an early dismissal to fit work and after-school sports around the school day",
  reasonHint:
    "The strongest reason or evidence they would give. Optional, except when you turn the tables.",
  responseLabel: "Your response or evidence",
  responsePlaceholder:
    "e.g. when Seattle moved its high schools' start time to 8:45 a.m., students slept 34 minutes longer each night (Dunster et al., 2018)",
  responseHint:
    "Evidence, a counterexample, or reasoning that meets their point head on. Keep the citation in.",
  tryExample: "Try an example",
  clear: "Clear",
  fieldNames: {
    claim: "your claim",
    opposing: "the opposing view",
    reason: "their best reason",
    response: "your response",
  },
  missingNotice: "Add {fields} to build the paragraph.",
  and: "and",
  resultsHeading: "Your counterargument paragraph",
  resultsIntro:
    "Three wordings of the same move. Pick one, then rewrite it in your own voice — honest templates, no AI.",
  versionLabel: "Version {n}",
  showLabels: "Show what each sentence does",
  capitalsTip:
    "Check capitals on names: the first word of a field is lowercased when it moves mid-sentence, unless you capitalize it elsewhere in your text.",
  copy: "Copy",
  copied: "Copied!",
  copyList: "Copy list",
  partLabels: {
    opposing: "Opposing view",
    reason: "Their reason",
    concession: "Concession",
    pivot: "The turn",
    rebuttal: "Rebuttal",
    claim: "Back to your claim",
  },
  bankHeading: "Sentence frames to adapt",
  bankIntro:
    "Fill the blanks with your own words when you want a different shape from the paragraphs above.",
  bankGroups: {
    concession: "Conceding a point",
    rebuttal: "Answering the objection",
    return: "Returning to your claim",
  },
  concessionFrames: COUNTER_CONCESSION_FRAMES,
  rebuttalFrames: COUNTER_REBUTTAL_FRAMES,
  returnFrames: COUNTER_RETURN_FRAMES,
  frames: COUNTER_FRAMES,
  /** Per strategy: claim, opposing view, their reason, your response. */
  examples: {
    concede: [
      "schools should start no earlier than 8:30 a.m.",
      "early start times should be kept",
      "many families rely on an early dismissal to fit work, childcare, and after-school sports around the school day",
      "when Seattle moved its high schools' start time from 7:50 to 8:45 a.m. in 2016, students slept a median of 34 minutes longer each night, and median grades in the classes studied rose by 4.5 percent (Dunster et al., 2018)",
    ],
    partial: [
      "schools should start no earlier than 8:30 a.m.",
      "early start times should be kept",
      "many families rely on an early dismissal to fit work, childcare, and after-school sports around the school day",
      "a district can protect those schedules by moving elementary schools to the earlier bus slot, shifting practice times, and keeping supervised programs open until parents finish work",
    ],
    turn: [
      "schools should start no earlier than 8:30 a.m.",
      "early start times should be kept",
      "an early dismissal leaves teenagers the afternoon for sports, jobs, and homework",
      "those afternoon hours help only if students are rested enough to use them, and because the body clock shifts later in adolescence (American Academy of Pediatrics, 2014), a 7:30 a.m. start leaves many teenagers short of sleep for the very activities the early schedule is meant to protect",
    ],
  },
} as const;

type Strings = typeof COUNTERARGUMENT_GENERATOR_STRINGS;

const EMPTY_INPUT: CounterInput = {
  claim: "",
  opposing: "",
  reason: "",
  response: "",
};

const FIELD_ORDER: readonly CounterField[] = [
  "claim",
  "opposing",
  "reason",
  "response",
];

const BADGE =
  "inline-flex h-fit w-fit rounded-md bg-primary/10 px-2 py-0.5 font-medium text-primary text-xs";

function missingNotice(
  strings: Strings,
  missing: readonly CounterField[],
  locale: string
) {
  const names = missing.map((field) => strings.fieldNames[field]);
  return t(strings.missingNotice, {
    fields: joinSeries(names, strings.and, locale),
  });
}

function FrameList({
  title,
  frames,
  strings,
}: {
  title: string;
  frames: readonly string[];
  strings: Strings;
}) {
  return (
    <div className={`${CARD} space-y-3`}>
      <div className="flex items-start justify-between gap-3">
        <h3 className="font-semibold text-foreground text-sm">{title}</h3>
        <CopyButton
          copiedLabel={strings.copied}
          label={strings.copyList}
          small
          text={frames.join("\n")}
        />
      </div>
      <ul className="space-y-1.5 text-foreground text-sm leading-relaxed">
        {frames.map((frame) => (
          <li key={frame}>{frame}</li>
        ))}
      </ul>
    </div>
  );
}

export default function CounterargumentGeneratorWidget() {
  const strings = useToolUi(COUNTERARGUMENT_GENERATOR_STRINGS);
  const fmt = useToolNumber();
  const locale = useToolLocale();
  const [strategy, setStrategy] = useState<CounterStrategy>("concede");
  const [input, setInput] = useState<CounterInput>(EMPTY_INPUT);
  const [showLabels, setShowLabels] = useState(true);

  const missing = missingCounterFields(input, strategy);
  const paragraphs =
    missing.length === 0
      ? buildCounterParagraphs(input, strategy, strings.frames, locale)
      : [];

  function update(field: CounterField, value: string) {
    setInput((previous) => ({ ...previous, [field]: value }));
  }

  function loadExample() {
    const [claim, opposing, reason, response] = strings.examples[strategy];
    setInput({ claim, opposing, reason, response });
  }

  const strategyOptions = COUNTER_STRATEGIES.map((value) => ({
    value,
    label: strings.strategies[value],
  }));

  const labels: Record<CounterField, string> = {
    claim: strings.claimLabel,
    opposing: strings.opposingLabel,
    reason: strings.reasonLabel,
    response: strings.responseLabel,
  };
  const placeholders: Record<CounterField, string> = {
    claim: strings.claimPlaceholder,
    opposing: strings.opposingPlaceholder,
    reason: strings.reasonPlaceholder,
    response: strings.responsePlaceholder,
  };
  const hints: Record<CounterField, string> = {
    claim: strings.claimHint,
    opposing: strings.opposingHint,
    reason: strings.reasonHint,
    response: strings.responseHint,
  };

  return (
    <>
      <div className={`${CARD} space-y-5`}>
        <PillGroup
          label={strings.strategyLabel}
          name="cag-strategy"
          onChange={setStrategy}
          options={strategyOptions}
          value={strategy}
        />
        <p className={HINT}>{strings.strategyHints[strategy]}</p>
        {FIELD_ORDER.map((field) =>
          field === "claim" || field === "opposing" ? (
            <TextField
              hint={hints[field]}
              id={`cag-${field}`}
              key={field}
              label={labels[field]}
              onChange={(value) => update(field, value)}
              placeholder={placeholders[field]}
              value={input[field]}
            />
          ) : (
            <TextAreaField
              hint={hints[field]}
              id={`cag-${field}`}
              key={field}
              label={labels[field]}
              onChange={(value) => update(field, value)}
              placeholder={placeholders[field]}
              prose
              rows={3}
              value={input[field]}
            />
          )
        )}
        <div className="flex flex-wrap gap-3">
          <button
            className={BUTTON_SECONDARY}
            onClick={loadExample}
            type="button"
          >
            {strings.tryExample}
          </button>
          <button
            className={BUTTON_SECONDARY}
            onClick={() => setInput(EMPTY_INPUT)}
            type="button"
          >
            {strings.clear}
          </button>
        </div>
      </div>

      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.resultsHeading}</h2>
        {paragraphs.length === 0 ? (
          <Notice>{missingNotice(strings, missing, locale)}</Notice>
        ) : (
          <>
            <p className="text-muted-foreground text-sm">
              {strings.resultsIntro}
            </p>
            <Checkbox
              checked={showLabels}
              label={strings.showLabels}
              onChange={setShowLabels}
            />
            {paragraphs.map((sentences, index) => (
              <div
                className={`${RESULT_CARD} space-y-3`}
                key={`${strategy}-${index + 1}`}
              >
                <div className="flex items-center justify-between gap-3">
                  <h3 className={WIDGET_H3}>
                    {t(strings.versionLabel, { n: fmt(index + 1) })}
                  </h3>
                  <CopyButton
                    copiedLabel={strings.copied}
                    label={strings.copy}
                    small
                    text={paragraphText(sentences, locale)}
                  />
                </div>
                {showLabels ? (
                  <ol className="space-y-2">
                    {sentences.map((sentence) => (
                      <li
                        className="grid gap-1 sm:grid-cols-[9.5rem_1fr] sm:gap-3"
                        key={sentence.part}
                      >
                        <span className={BADGE}>
                          {strings.partLabels[sentence.part]}
                        </span>
                        <span className="text-foreground text-sm leading-relaxed">
                          {sentence.text}
                        </span>
                      </li>
                    ))}
                  </ol>
                ) : (
                  <p className="text-foreground text-sm leading-relaxed">
                    {paragraphText(sentences, locale)}
                  </p>
                )}
              </div>
            ))}
            <p className={HINT}>{strings.capitalsTip}</p>
          </>
        )}
      </section>

      <section className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.bankHeading}</h2>
        <p className="text-muted-foreground text-sm">{strings.bankIntro}</p>
        <div className="grid gap-4 md:grid-cols-3">
          <FrameList
            frames={strings.concessionFrames}
            strings={strings}
            title={strings.bankGroups.concession}
          />
          <FrameList
            frames={strings.rebuttalFrames}
            strings={strings}
            title={strings.bankGroups.rebuttal}
          />
          <FrameList
            frames={strings.returnFrames}
            strings={strings}
            title={strings.bankGroups.return}
          />
        </div>
      </section>
    </>
  );
}
